import type { SdkUser } from '@dynamic-labs/sdk-api-core';
import { JwtVerifiedCredentialFormatEnum } from '@dynamic-labs/sdk-api-core';

import type { DynamicClient } from '../../../../client/types';
import type { UnverifiedWalletAccount } from '../../unverifiedWalletAccounts';
import { convertUnverifiedWalletAccountToWalletAccount } from '../../utils/convertUnverifiedWalletAccountToWalletAccount';
import { convertVerifiedCredentialToWalletAccount } from '../../utils/convertVerifiedCredentialToWalletAccount';
import type { WalletAccount } from '../../walletAccount';

type GetWalletAccountsFromStateParams = {
  unverifiedWalletAccounts: UnverifiedWalletAccount[];
  user: SdkUser | null;
};

export const getWalletAccountsFromState = (
  { unverifiedWalletAccounts, user }: GetWalletAccountsFromStateParams,
  client: DynamicClient
): WalletAccount[] => {
  const walletAccountsMap: Map<string, WalletAccount> = new Map();

  /**
   * Handle the unverified wallet accounts before the user verified credentials
   * so the later verified wallet accounts can override the unverified wallet accounts
   */
  unverifiedWalletAccounts.forEach((unverifiedWalletAccount) => {
    const walletAccount = convertUnverifiedWalletAccountToWalletAccount({
      unverifiedWalletAccount,
    });

    walletAccountsMap.set(walletAccount.id, walletAccount);
  });

  /**
   * Handle user verified credentials
   */
  const verifiedCredentials = user?.verifiedCredentials ?? [];

  verifiedCredentials
    .filter(
      (verified) =>
        verified.format === JwtVerifiedCredentialFormatEnum.Blockchain
    )
    .forEach((verifiedWalletAccount) => {
      const walletAccount = convertVerifiedCredentialToWalletAccount(
        {
          verifiedCredential: verifiedWalletAccount,
        },
        client
      );

      walletAccountsMap.set(walletAccount.id, walletAccount);
    });

  return Array.from(walletAccountsMap.values());
};
